/**
 * The searchable country list behind both country fields — the phone number's
 * dial code (`PhoneInput`) and nationality (`CountrySelect`). One list, one
 * keyboard model, one sheet, so the two fields cannot drift apart.
 *
 * A combobox: the search field holds focus and `aria-activedescendant` names the
 * highlighted row; arrows move it, Enter chooses, Escape or an outside tap closes.
 * It opens ON the selected country, highlighted and scrolled into view — in a
 * 230-row list the applicant should see what is chosen first.
 *
 * Below 768px it opens as a BOTTOM SHEET instead of a dropdown (the pattern
 * MessageEagle's input uses on phones): a dropdown under a field near the bottom
 * of a phone screen has nowhere to open. The sheet is portalled to <body> — the
 * step card animates with `transform`, which would make a fixed sheet position
 * against the card instead of the viewport — and it does not focus the search
 * field, so the keyboard does not cover the list on open.
 */
import { type RefObject, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { countries, flagUrl, searchCountries } from './phoneNumber';
import { useNarrowViewport } from './viewport';

interface CountryPickerProps {
  open: boolean;
  onClose: () => void;
  /** The chosen ISO code, or '' for none yet. */
  selected: string;
  onChoose: (code: string) => void;
  /** The field the list belongs to — a tap inside it is not an outside tap. */
  anchor: RefObject<HTMLElement | null>;
  /** Show each row's dial code (the phone field) or not (nationality). */
  showDial: boolean;
  /** Heading of the sheet on a phone-sized screen. */
  title: string;
}

export function CountryPicker({
  open,
  onClose,
  selected,
  onChoose,
  anchor,
  showDial,
  title,
}: CountryPickerProps) {
  const { t, i18n } = useTranslation();
  const list = useMemo(() => countries(i18n.language), [i18n.language]);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const search = useRef<HTMLInputElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const listId = useId();
  const asSheet = useNarrowViewport();
  const results = useMemo(() => searchCountries(list, query), [list, query]);

  // Every opening starts clean, on the selected country.
  const [openedFor, setOpenedFor] = useState<boolean>(false);
  if (open !== openedFor) {
    setOpenedFor(open);
    if (open) {
      setQuery('');
      setActive(Math.max(0, list.findIndex((c) => c.code === selected)));
    }
  }

  useEffect(() => {
    if (!open) return;
    if (!asSheet) search.current?.focus();
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      const inside = anchor.current?.contains(target) || sheet.current?.contains(target);
      if (!inside) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', escape);
    // The page behind a sheet must not scroll under the applicant's thumb.
    const overflow = document.body.style.overflow;
    if (asSheet) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', escape);
      document.body.style.overflow = overflow;
    };
  }, [open, asSheet, anchor, onClose]);

  // Keep the highlighted row in view as the arrows move it (and on open).
  useEffect(() => {
    if (!open) return;
    const row = results[active] ? document.getElementById(`${listId}-${results[active].code}`) : null;
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, results, listId]);

  if (!open) return null;

  const onListKey = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = results[active];
      if (picked) onChoose(picked.code);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  const picker = (
    <>
      <input
        ref={search}
        className="country-picker__search"
        type="search"
        role="combobox"
        aria-expanded="true"
        aria-controls={listId}
        aria-activedescendant={results[active] ? `${listId}-${results[active].code}` : undefined}
        aria-label={t('flow.countryPicker.search')}
        placeholder={t('flow.countryPicker.search')}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        onKeyDown={onListKey}
      />
      <ul
        className="country-picker__list"
        role="listbox"
        id={listId}
        aria-label={t('flow.countryPicker.search')}
      >
        {results.length === 0 ? (
          <li className="country-picker__empty" role="presentation">
            {t('flow.countryPicker.noMatch')}
          </li>
        ) : (
          results.map((c, index) => (
            <li
              key={c.code}
              id={`${listId}-${c.code}`}
              role="option"
              aria-selected={c.code === selected}
              className={`country-picker__option${index === active ? ' is-active' : ''}`}
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => onChoose(c.code)}
              onKeyDown={onListKey}
              onMouseEnter={() => setActive(index)}
            >
              <img src={flagUrl(c.code)} alt="" width={22} height={22} loading="lazy" />
              <span className="country-picker__name">{c.name}</span>
              {showDial ? <span className="country-picker__code">{c.dial}</span> : null}
            </li>
          ))
        )}
      </ul>
    </>
  );

  if (!asSheet) return <div className="country-picker__popover">{picker}</div>;

  return createPortal(
    <div className="country-sheet">
      <div className="country-sheet__backdrop" aria-hidden="true" />
      <div
        ref={sheet}
        className="country-sheet__panel country-sheet__panel--list"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="country-sheet__handle" aria-hidden="true" />
        <div className="country-sheet__header">
          <h2 className="country-sheet__title">{title}</h2>
          <button
            type="button"
            className="country-sheet__close"
            aria-label={t('flow.countryPicker.close')}
            data-track="close_picker"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
        {picker}
      </div>
    </div>,
    document.body,
  );
}

/** The up/down chevron both country triggers draw. */
export function Chevrons() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path d="M8 9l4-4 4 4M8 15l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
